import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { formService } from '../../../services/formService';

const PartnershipInquiry = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    partnershipType: '',
    industry: '',
    description: '',
    website: ''
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const partnershipTypeOptions = [
    { value: 'strategic', label: 'Strategic Partnership' },
    { value: 'technology', label: 'Technology Collaboration' },
    { value: 'investment', label: 'Investment Opportunity' },
    { value: 'joint-venture', label: 'Joint Venture' },
    { value: 'reseller', label: 'Reseller/Distribution' },
    { value: 'affiliate', label: 'Affiliate Program' },
    { value: 'other', label: 'Other Partnership' }
  ];

  const industryOptions = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'finance', label: 'Finance & Fintech' },
    { value: 'healthcare', label: 'Healthcare & Medical' },
    { value: 'education', label: 'Education & E-learning' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'media', label: 'Media & Entertainment' },
    { value: 'other', label: 'Other Industry' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e?.target?.files);
    const validFiles = selectedFiles?.filter(file => {
      const isValidType = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png']?.includes(file?.type);
      const isValidSize = file?.size <= 10 * 1024 * 1024;
      return isValidType && isValidSize;
    });

    setFiles(prev => [...prev, ...validFiles]?.slice(0, 3));
  };

  const removeFile = (index) => {
    setFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.contactPerson?.trim()) {
      newErrors.contactPerson = 'Contact person name is required';
    }

    if (!formData?.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Valid email address is required';
    }

    if (!formData?.partnershipType) {
      newErrors.partnershipType = 'Please select partnership type';
    }

    if (!formData?.industry) {
      newErrors.industry = 'Please select your industry';
    }

    if (!formData?.description?.trim() || formData?.description?.trim()?.length < 50) {
      newErrors.description = 'Please provide at least 50 characters describing the partnership opportunity';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await formService?.submitPartnershipForm(formData);
      
      setSuccess(true);

      setTimeout(() => {
        setSuccess(false);
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          partnershipType: '',
          industry: '',
          description: '',
          website: ''
        });
        setFiles([]);
      }, 5000);
    } catch (error) {
      setErrors({ submit: error?.message || 'Failed to submit inquiry. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-card rounded-xl p-6 md:p-8 shadow-lg border border-border">
        <div className="flex flex-col items-center justify-center text-center py-8 md:py-12">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/10 flex items-center justify-center mb-4 md:mb-6">
            <Icon name="CheckCircle2" size={40} color="var(--color-success)" />
          </div>
          <h3 className="text-2xl md:text-3xl font-semibold mb-3 md:mb-4">Partnership Inquiry Submitted!</h3>
          <p className="text-muted-foreground text-sm md:text-base mb-6 md:mb-8 max-w-md">
            Thank you for your interest in partnering with us. Our business development team will review your proposal and contact you within 3-5 business days.
          </p>
          <Button
            variant="default"
            onClick={() => setSuccess(false)}
            iconName="Briefcase"
            iconPosition="left"
          >
            Submit Another Inquiry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-lg border border-border">
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-3 mb-3 md:mb-4">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <Icon name="Handshake" size={24} color="white" />
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">Partnership Opportunities</h2>
            <p className="text-sm text-muted-foreground">Let's explore collaboration possibilities</p>
          </div>
        </div>
        <p className="text-sm md:text-base text-muted-foreground">
          I'm always open to strategic partnerships, technology collaborations, and business opportunities that create mutual value.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Company Name"
            type="text"
            placeholder="Your company name"
            value={formData?.companyName}
            onChange={(e) => handleInputChange('companyName', e?.target?.value)}
            error={errors?.companyName}
            required
          />

          <Input
            label="Contact Person"
            type="text"
            placeholder="Your full name"
            value={formData?.contactPerson}
            onChange={(e) => handleInputChange('contactPerson', e?.target?.value)}
            error={errors?.contactPerson}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Input
            label="Business Email"
            type="email"
            placeholder="company@example.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />

          <Input
            label="Phone Number"
            type="tel"
            placeholder="+234 XXX XXX XXXX"
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <Select
            label="Partnership Type"
            placeholder="Select partnership type"
            options={partnershipTypeOptions}
            value={formData?.partnershipType}
            onChange={(value) => handleInputChange('partnershipType', value)}
            error={errors?.partnershipType}
            required
          />

          <Select
            label="Industry"
            placeholder="Select your industry"
            options={industryOptions}
            value={formData?.industry}
            onChange={(value) => handleInputChange('industry', value)}
            error={errors?.industry}
            required
          />
        </div>

        <Input
          label="Company Website (Optional)"
          type="url"
          placeholder="https://www.yourcompany.com"
          value={formData?.website}
          onChange={(e) => handleInputChange('website', e?.target?.value)}
        />

        <div>
          <label className="block text-sm font-medium mb-2">
            Partnership Description <span className="text-error">*</span>
          </label>
          <textarea
            className="w-full min-h-[150px] md:min-h-[180px] px-4 py-3 rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            placeholder="Describe your partnership proposal, expected outcomes, timeline, and how this collaboration would benefit both parties..."
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
          />
          {errors?.description && (
            <p className="text-sm text-error mt-1">{errors?.description}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Supporting Documents (Optional)
          </label>
          <div className="border-2 border-dashed border-border rounded-lg p-6 md:p-8 text-center hover:border-primary transition-colors">
            <input
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-sm font-medium mb-1">Click to upload or drag and drop</p>
              <p className="text-xs text-muted-foreground">PDF, DOC, DOCX, JPG, PNG (max 10MB each, up to 3 files)</p>
            </label>
          </div>

          {files?.length > 0 && (
            <div className="mt-4 space-y-2">
              {files?.map((file, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Icon name="FileText" size={20} color="var(--color-primary)" className="flex-shrink-0" />
                    <span className="text-sm truncate">{file?.name}</span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="ml-2 p-1 hover:bg-destructive/10 rounded transition-colors flex-shrink-0"
                  >
                    <Icon name="X" size={16} color="var(--color-destructive)" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <Button
          type="submit"
          variant="default"
          loading={loading}
          fullWidth
          iconName="Send"
          iconPosition="right"
        >
          Submit Partnership Inquiry
        </Button>
      </form>
      <div className="mt-6 md:mt-8 p-4 md:p-6 rounded-lg bg-primary/5 border border-primary/20">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-1" />
          <div>
            <h4 className="text-sm font-semibold mb-2">What happens next?</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Our team reviews your proposal within 3-5 business days</li>
              <li>• We schedule an initial consultation call if there's mutual interest</li>
              <li>• We discuss partnership terms, expectations, and next steps</li>
              <li>• We formalize the partnership with proper documentation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnershipInquiry;